// src/components/ProductCarousel.js
import React, { useState, useEffect, useRef } from 'react';
import { getProducts, getPublicStore } from '../api/apiService';
import ProductCard from './ProductCard';

const ProductCarousel = ({ onProductClick }) => {
  const [products,    setProducts]    = useState([]);
  const [publicStore, setPublicStore] = useState(null);
  const [activeIdx,   setActiveIdx]   = useState(0);

  const trackRef   = useRef(null);
  const touchStart = useRef(null);
  const touchDelta = useRef(0);
  const isDragging = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const store = await getPublicStore();
        if (!store) return;
        setPublicStore(store);
        const sid = String(store.id);
        const all = await getProducts();
        setProducts(
          all.filter(p =>
            Array.isArray(p.assignedStores) &&
            p.assignedStores.map(String).includes(sid)
          )
        );
      } catch (e) {
        console.error('Error cargando carrusel:', e);
      }
    })();
  }, []);

  const total = products.length;
  if (!total || !publicStore) return null;

  // Cuántas tarjetas mostrar según ancho
  const getVisible = () => {
    if (typeof window === 'undefined') return 3;
    if (window.innerWidth < 480) return 1;
    if (window.innerWidth < 768) return 2;
    return 3;
  };
  const visible = getVisible();
  const maxIdx  = Math.max(0, total - visible);

  const goTo = (idx) => {
    const clamped = Math.max(0, Math.min(idx, maxIdx));
    setActiveIdx(clamped);
  };

  // ── Touch handlers ──────────────────────────────────────────────────────
  const onTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
    touchDelta.current = 0;
    isDragging.current = true;
  };

  const onTouchMove = (e) => {
    if (!isDragging.current) return;
    touchDelta.current = e.touches[0].clientX - touchStart.current;
  };

  const onTouchEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const threshold = 50;
    if (touchDelta.current < -threshold) goTo(activeIdx + 1);
    else if (touchDelta.current > threshold) goTo(activeIdx - 1);
    touchDelta.current = 0;
  };

  // ── Mouse drag (desktop) ────────────────────────────────────────────────
  const onMouseDown = (e) => {
    touchStart.current = e.clientX;
    touchDelta.current = 0;
    isDragging.current = true;
  };

  const onMouseMove = (e) => {
    if (!isDragging.current) return;
    touchDelta.current = e.clientX - touchStart.current;
  };

  const onMouseUp = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const threshold = 60;
    if (touchDelta.current < -threshold) goTo(activeIdx + 1);
    else if (touchDelta.current > threshold) goTo(activeIdx - 1);
    touchDelta.current = 0;
  };

  const cardW    = 100 / visible;
  const offsetPct = activeIdx * cardW;

  return (
    <div>
      {/* Pista deslizable */}
      <div style={{ overflow: 'hidden', cursor: 'grab', userSelect: 'none' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}>

        <div ref={trackRef}
          style={{
            display: 'flex',
            transform: `translateX(-${offsetPct}%)`,
            transition: isDragging.current ? 'none' : 'transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94)',
            willChange: 'transform',
          }}>
          {products.map((product, i) => (
            <div key={product.id}
              style={{
                flex: `0 0 ${cardW}%`,
                padding: '0 8px',
                boxSizing: 'border-box',
                pointerEvents: isDragging.current ? 'none' : 'auto',
              }}>
              <ProductCard product={product} onProductClick={onProductClick} />
            </div>
          ))}
        </div>
      </div>

      {/* Dots */}
      {total > visible && (
        <div style={{ display: 'flex', justifyContent: 'center',
          gap: '6px', marginTop: '24px' }}>
          {Array.from({ length: maxIdx + 1 }, (_, idx) => (
            <button key={idx} onClick={() => goTo(idx)} style={{
              height: '3px',
              width: activeIdx === idx ? '24px' : '8px',
              background: activeIdx === idx ? '#961914' : '#ddd',
              border: 'none', cursor: 'pointer',
              transition: 'all 0.3s', borderRadius: '2px', padding: 0,
            }} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductCarousel;
